import { Dispatch, useState } from "react";
import { QueryClient, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { toast } from "@/design-system/components/Toast/Toast";

import useAccountMetadata from "@/hooks/useAccountMetadata";
import { setCurrentEnvironment } from "@/actions/environments";
import {
  delayAsync,
  makeRequest,
  masqueradeAs,
  masqueradeToken,
  masqueradingMinutesLeft,
  stopMasquerading,
} from "@/util";
import { Button } from "@/design-system/components/Button/Button";
import { AutocompleteMasquerade } from "./AutocompleteMasquerade";

type MasqueradeAttemptResponse = {
  data: {
    targetAccountId: string;
    targetType: string;
  };
};

type MasqueradeStartType = {
  masqueradeTarget: string;
  targetHref?: string;
  dispatch: Dispatch<any>;
  queryClient: QueryClient;
};

// Assume the role of another account.
// targetHref is the URL to redirect to after masquerading. Can be undefined, in which
// case the current page will just be reloaded.
export async function handleStartMasquerading({
  dispatch,
  queryClient,
  masqueradeTarget,
  targetHref,
}: MasqueradeStartType) {
  toast.show("Attempting to masquerade");
  return makeRequest("attempt_masquerade", {
    masquerade_value: masqueradeTarget,
  })
    .then(async (response) => {
      const typedResponse = response as MasqueradeAttemptResponse;
      const { targetAccountId, targetType } = typedResponse.data;
      masqueradeAs(masqueradeTarget);
      if (targetType === "ENV_ID") {
        dispatch(setCurrentEnvironment(masqueradeTarget, queryClient));
      }
      toast.success(`You are now masquerading as account ${targetAccountId}`);
      await delayAsync(1000);
      if (targetHref) {
        window.open(targetHref, "_blank");
      } else {
        window.location.reload();
      }
    })
    .catch((error) => {
      toast.error(
        `Failed to masquerade.
         Please double check your provided input`,
      );
      console.error(error);
    });
}

export const handleStopMasquerading = () => {
  stopMasquerading();
  toast.success("You are no longer masquerading");
  setTimeout(() => window.location.reload(), 1000);
};

export const MasqueradeComponent = () => {
  const params = new URLSearchParams(useLocation().search);
  const [masqueradeTarget, setMasqueradeTarget] = useState(
    params.get("account") || "",
  );
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const whoAmI = masqueradeToken();
  const minutesLeft = masqueradingMinutesLeft();

  const accountMetadata = useAccountMetadata();
  const account_id = accountMetadata.accountID;

  return (
    <div className="mb-10">
      <h1 className="mb-4">Masquerade</h1>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p>
            {whoAmI && (
              <>
                You are currently masquerading as <strong>{whoAmI}</strong>
                .{" "}
              </>
            )}
            Masquerade sessions last two hours, after which you will be returned
            to your original account.
          </p>
          {minutesLeft && (
            <p>
              The current masquerade session will expire in {minutesLeft}{" "}
              minutes.
            </p>
          )}
          {account_id && <p>Current Account ID: {accountMetadata.accountID}</p>}
        </div>

        <AutocompleteMasquerade
          placeholder="Email or Account, Environment, or Project ID"
          value={masqueradeTarget}
          onChange={setMasqueradeTarget}
          className="w-full max-w-2xl rounded-md border p-4"
        />

        <div className="flex gap-4">
          <Button
            onClick={() =>
              handleStartMasquerading({
                dispatch,
                queryClient,
                masqueradeTarget,
                targetHref: undefined,
              })
            }
          >
            Start Masquerading
          </Button>
          <Button variant="outline" onClick={() => handleStopMasquerading()}>
            Stop Masquerading
          </Button>
        </div>
      </div>
    </div>
  );
};
